import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import {
  BookOpenIcon,
  MagnifyingGlassIcon,
  MapIcon,
  PencilIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import axios from "../../../api/axios";
import { fetchProject } from "../../../api/project";
import { DrawingStructureTree } from "../../../components/dashboard/drawings/drawing-structure-tree";
import { ProjectSwitcher } from "../../../components/dashboard/projects/ProjectSwitcher";
import { SheetShell } from "../../../components/dashboard/drawings/drawing-mobile-sheet";
import { DrawingCreateDrawer } from "../../../components/dashboard/drawings/drawing-create-drawer";
import { formatDrawingLocation } from "../../../components/dashboard/drawings/drawingStructureApi";
import {
  buildDrawingListSearchParams,
  readDrawingListState,
  writeDrawingListState,
} from "../../../components/dashboard/drawings/drawingStructureState";
import {
  DRAWING_KINDS,
  drawingKindLabel,
} from "../../../components/dashboard/drawings/drawingKinds";
import { drawingKindStyle } from "../../../components/dashboard/drawings/drawingKindStyles";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../components/utils/ResourceError";
import { useUserHasAccess } from "../../../routes/roleOnly";

const LIST_TABS = [
  { id: "all", labelKey: "drawings.list_ui.tab_all" },
  { id: "needs_review", labelKey: "drawings.list_ui.tab_needs_review" },
  { id: "revisions", labelKey: "drawings.list_ui.tab_revisions" },
  { id: "archive", labelKey: "drawings.list_ui.tab_archive" },
];

const formatRelativeUpdated = (t, value) => {
  if (!value) return t("drawings.list_ui.updated_unknown");
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return t("drawings.list_ui.updated_unknown");
  const diffMs = Date.now() - date.getTime();
  const day = 24 * 60 * 60 * 1000;
  if (diffMs < day) return t("drawings.list_ui.updated_today");
  if (diffMs < 2 * day) return t("drawings.list_ui.updated_yesterday");
  const days = Math.floor(diffMs / day);
  if (days < 14) return t("drawings.list_ui.updated_days", { count: days });
  return date.toLocaleDateString();
};

const parseProjectCodeName = (displayTitle = "") => {
  const match = String(displayTitle).match(/^(\d+)\s+(.+)$/);
  if (match) return { code: match[1], name: match[2] };
  return { code: "", name: displayTitle };
};

export const DrawingsList = () => {
  const { t } = useTranslation();
  const canEditDrawings = useUserHasAccess({ role: "drawings_edit" });
  const [searchParams, setSearchParams] = useSearchParams();
  const initialListStateRef = useRef(readDrawingListState());
  const [drawings, setDrawings] = useState([]);
  const [structureIndex, setStructureIndex] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [searchQuery, setSearchQuery] = useState(
    () => initialListStateRef.current.searchQuery || "",
  );
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(
    () => initialListStateRef.current.searchQuery || "",
  );
  const [selectedProject, setSelectedProject] = useState(() => {
    // Sidebar entry uses ?select_project=1 → always start at project gate.
    if (
      typeof window !== "undefined" &&
      new URLSearchParams(window.location.search).get("select_project") === "1"
    ) {
      return null;
    }
    const stored = initialListStateRef.current;
    if (!stored.projectId) return null;
    return {
      id: stored.projectId,
      displayTitle: stored.projectTitle || "",
    };
  });
  const [selectedFloorId, setSelectedFloorId] = useState(
    () => initialListStateRef.current.selectedFloorId ?? null,
  );
  const [selectedHouseId, setSelectedHouseId] = useState(
    () => initialListStateRef.current.selectedHouseId ?? null,
  );
  const [selectedAndre, setSelectedAndre] = useState(() =>
    Boolean(initialListStateRef.current.selectedAndre),
  );
  const [kindFilter, setKindFilter] = useState(null);
  const [listTab, setListTab] = useState("all");
  const [manageStructure, setManageStructure] = useState(false);
  const [structureOpen, setStructureOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(
    () => searchParams.get("new") === "1",
  );

  const hasSelectedProject = Boolean(selectedProject?.id);
  const effectiveSelectedAndre = selectedAndre && hasSelectedProject;
  const selectedAll =
    hasSelectedProject &&
    !effectiveSelectedAndre &&
    !selectedFloorId &&
    !selectedHouseId;
  const needsProject = !hasSelectedProject;

  const syncUrl = useCallback(
    (state) => {
      const next = buildDrawingListSearchParams(state);
      if (state.openNew) next.set("new", "1");
      if (next.toString() !== searchParams.toString()) {
        setSearchParams(next, { replace: true });
      }
    },
    [searchParams, setSearchParams],
  );

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchQuery(searchQuery), 250);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    syncUrl({
      query: debouncedSearchQuery,
      projectId: selectedProject?.id ?? null,
      drawingHouseId: effectiveSelectedAndre ? null : selectedHouseId,
      drawingFloorId: effectiveSelectedAndre ? null : selectedFloorId,
      unassigned: effectiveSelectedAndre,
      openNew: createOpen,
    });
    writeDrawingListState({
      searchQuery: debouncedSearchQuery,
      projectId: selectedProject?.id ?? null,
      projectTitle: selectedProject?.displayTitle ?? "",
      selectedFloorId: effectiveSelectedAndre ? null : selectedFloorId,
      selectedHouseId: effectiveSelectedAndre ? null : selectedHouseId,
      selectedAndre: effectiveSelectedAndre,
    });
  }, [
    debouncedSearchQuery,
    selectedProject,
    selectedFloorId,
    selectedHouseId,
    selectedAndre,
    effectiveSelectedAndre,
    createOpen,
    syncUrl,
  ]);

  useEffect(() => {
    if (!hasSelectedProject && selectedAndre) setSelectedAndre(false);
  }, [hasSelectedProject, selectedAndre]);

  useEffect(() => {
    if (searchParams.get("new") === "1") setCreateOpen(true);
  }, [searchParams]);

  // Menu → Tegninger: force project picker and drop persisted project for this visit.
  useEffect(() => {
    if (searchParams.get("select_project") !== "1") return;
    setSelectedProject(null);
    setSelectedFloorId(null);
    setSelectedHouseId(null);
    setSelectedAndre(false);
    writeDrawingListState({
      searchQuery: debouncedSearchQuery,
      projectId: null,
      projectTitle: "",
      selectedFloorId: null,
      selectedHouseId: null,
      selectedAndre: false,
    });
    const next = new URLSearchParams(searchParams);
    next.delete("select_project");
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams, debouncedSearchQuery]);

  useEffect(() => {
    const projectId = selectedProject?.id;
    if (!projectId || selectedProject.displayTitle) return;

    let cancelled = false;
    fetchProject(projectId, { fields: "id,displayTitle,name,project_number" })
      .then((response) => {
        if (cancelled) return;
        const project = response?.data;
        if (!project?.id) return;
        setSelectedProject({
          id: project.id,
          displayTitle:
            project.displayTitle ??
            [project.project_number, project.name].filter(Boolean).join(" ") ??
            "",
        });
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [selectedProject?.id, selectedProject?.displayTitle]);

  const fetchData = async () => {
    if (!hasSelectedProject) {
      setDrawings([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(false);
      const params = {
        query: debouncedSearchQuery,
        per_page: 50,
        page: 1,
        project_id: selectedProject.id,
      };
      if (effectiveSelectedAndre) params.unassigned = true;
      else if (selectedFloorId) params.drawing_floor_id = selectedFloorId;
      else if (selectedHouseId) params.drawing_house_id = selectedHouseId;
      if (kindFilter) params.drawing_kind = kindFilter;
      params.fields =
        "id,title,description,project,floor,house,drawing_kind,drawing_floor,drawing_floor_id,created_at,updated_at";

      const response = await axios({
        method: "get",
        url: "/drawings/",
        params,
      });
      setDrawings(response.data.data || []);
    } catch (err) {
      console.error("Error fetching drawings:", err);
      setError(true);
      toast.error(t("drawings.load_error"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [
    debouncedSearchQuery,
    selectedProject?.id,
    selectedFloorId,
    selectedHouseId,
    effectiveSelectedAndre,
    kindFilter,
    hasSelectedProject,
  ]);

  useEffect(() => {
    if (!hasSelectedProject) {
      setStructureIndex([]);
      return undefined;
    }
    let cancelled = false;
    axios({
      method: "get",
      url: "/drawings/",
      params: {
        project_id: selectedProject.id,
        per_page: 200,
        page: 1,
        fields: "id,drawing_kind,drawing_floor_id,drawing_floor",
      },
    })
      .then((response) => {
        if (!cancelled) setStructureIndex(response.data?.data || []);
      })
      .catch(() => {
        if (!cancelled) setStructureIndex([]);
      });
    return () => {
      cancelled = true;
    };
  }, [hasSelectedProject, selectedProject?.id, createOpen]);

  const kindsByFloorId = useMemo(() => {
    const map = {};
    structureIndex.forEach((row) => {
      const floorId = row.drawing_floor?.id || row.drawing_floor_id;
      if (!floorId) return;
      const kind = row.drawing_kind || "arkitekt";
      if (!map[floorId]) map[floorId] = [];
      if (!map[floorId].includes(kind)) map[floorId].push(kind);
    });
    return map;
  }, [structureIndex]);

  const totalCount = structureIndex.length;
  const unassignedCount = structureIndex.filter(
    (row) => !(row.drawing_floor?.id || row.drawing_floor_id),
  ).length;

  const handleDelete = async (id) => {
    if (!canEditDrawings) return;
    if (!window.confirm(t("drawings.delete_confirm"))) return;

    try {
      await axios({
        method: "delete",
        url: `/drawings/${id}`,
      });
      toast.success(t("drawings.deleted"));
      setDrawings((current) => current.filter((drawing) => drawing.id !== id));
      setStructureIndex((current) =>
        current.filter((drawing) => drawing.id !== id),
      );
    } catch (err) {
      console.error("Error deleting drawing:", err);
      toast.error(t("drawings.delete_error"));
    }
  };

  const handleSelectFloor = (floorId) => {
    setSelectedFloorId(floorId);
    setSelectedAndre(false);
    if (floorId) setSelectedHouseId(null);
  };

  const handleSelectHouse = (houseId) => {
    setSelectedHouseId(houseId);
    setSelectedAndre(false);
    if (houseId) setSelectedFloorId(null);
  };

  const handleSelectAndre = () => {
    if (!hasSelectedProject) return;
    setSelectedAndre(true);
    setSelectedFloorId(null);
    setSelectedHouseId(null);
  };

  const handleSelectAll = () => {
    setSelectedFloorId(null);
    setSelectedHouseId(null);
    setSelectedAndre(false);
  };

  const selectProject = (project) => {
    if (!project?.id) return;
    setSelectedProject({
      id: project.id,
      displayTitle:
        project.displayTitle ||
        [project.project_number, project.name].filter(Boolean).join(" ") ||
        project.name ||
        "",
    });
    setSelectedFloorId(null);
    setSelectedHouseId(null);
    setSelectedAndre(false);
  };

  const { code: projectCode, name: projectName } = parseProjectCodeName(
    selectedProject?.displayTitle || "",
  );

  const scopeLabel = effectiveSelectedAndre
    ? t("drawings.structure.andre")
    : selectedFloorId
      ? t("drawings.list_ui.scope_floor")
      : selectedHouseId
        ? t("drawings.list_ui.scope_house")
        : t("drawings.list_ui.all_drawings");

  if (needsProject) {
    return (
      <div className="flex min-h-screen flex-col bg-[#EEF0F3] text-[#12161C]">
        <div className="flex flex-1 items-start justify-center px-6 py-16">
          <div className="w-full max-w-[620px]">
            <div className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[#1F4BE0]">
              {t("drawings.list")}
            </div>
            <h1 className="mt-2 text-[30px] font-semibold tracking-[-0.02em]">
              {t("drawings.list_ui.pick_project_title")}
            </h1>
            <p className="mt-2 max-w-[460px] text-[14px] leading-relaxed text-[#5B6472]">
              {t("drawings.list_ui.pick_project_help")}
            </p>

            <div className="mt-6">
              <ProjectSwitcher inline onSelect={selectProject} />
            </div>
            <p className="mt-3.5 text-[12px] text-[#6B7482]">
              {t("drawings.list_ui.pick_project_footer")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-[#EEF0F3] text-[#12161C]">
      <div className="flex-none border-b border-[#DCE0E6] bg-white px-4 pt-4 md:px-6">
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2 text-[12px] text-[#6B7482]">
              <span>{t("drawings.list.projects")}</span>
              <span className="text-[#C2C8D0]">/</span>
              {projectCode ? (
                <span className="font-mono text-[#1F4BE0]">{projectCode}</span>
              ) : null}
              <ProjectSwitcher currentId={selectedProject.id} onSelect={selectProject}>
                {({ open, toggle }) => (
                  <button
                    type="button"
                    onClick={toggle}
                    aria-expanded={open}
                    aria-haspopup="dialog"
                    title={t("project.switcher.switch")}
                    className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[12px] font-medium text-[#12161C] ${
                      open
                        ? "border-[#e3e8ef] bg-[#f1f4f8]"
                        : "border-transparent hover:border-[#e3e8ef] hover:bg-[#f1f4f8]"
                    }`}
                  >
                    <span>{projectName || selectedProject.displayTitle}</span>
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="#6b7684" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      <path d="M4 6l4 4 4-4" />
                    </svg>
                  </button>
                )}
              </ProjectSwitcher>
              <span className="text-[#C2C8D0]">/</span>
              <span className="font-medium text-[#12161C]">
                {t("drawings.list")}
              </span>
            </div>
            <div className="mt-2 flex items-baseline gap-3">
              <h1 className="text-[24px] font-semibold tracking-[-0.02em]">
                {t("drawings.list")}
              </h1>
              <span className="text-[13px] text-[#6B7482]">
                {t("drawings.list_ui.summary", { count: totalCount })}
              </span>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 md:pt-2.5">
            <Link
              to={`/dashboard/projects/${selectedProject.id}`}
              className="inline-flex min-h-11 items-center rounded-[7px] border border-[#C9D6FA] bg-[#F3F6FF] px-3.5 py-2 text-[13px] font-medium text-[#1F4BE0] hover:bg-[#E6ECFF]"
            >
              {t("drawings.list_ui.go_to_project")}
            </Link>
            <button
              type="button"
              onClick={() => setStructureOpen(true)}
              className="inline-flex min-h-11 items-center gap-1.5 rounded-[7px] border border-[#C9D6FA] bg-[#F3F6FF] px-3.5 py-2 text-[13px] font-medium text-[#1F4BE0] md:hidden"
            >
              <MapIcon className="h-4 w-4" />
              {t("drawings.list_ui.open_structure")}
            </button>
            <Link
              to="/dashboard/guide/drawings"
              className="hidden min-h-11 items-center gap-1.5 rounded-[7px] border border-[#D3D8E0] bg-white px-3.5 py-2 text-[13px] font-medium text-[#37414F] hover:bg-[#F5F7FA] md:inline-flex"
            >
              <BookOpenIcon className="h-4 w-4" />
              {t("drawings.list_ui.open_user_guide")}
            </Link>
            <button
              type="button"
              onClick={() => setManageStructure((v) => !v)}
              className={`hidden min-h-11 rounded-[7px] border px-3.5 py-2 text-[13px] font-medium md:inline-flex ${
                manageStructure
                  ? "border-[#1F4BE0] bg-[#F3F6FF] text-[#1F4BE0]"
                  : "border-[#D3D8E0] bg-white text-[#37414F] hover:bg-[#F5F7FA]"
              }`}
            >
              {t("drawings.list_ui.manage_structure")}
            </button>
            {canEditDrawings ? (
              <button
                type="button"
                onClick={() => setCreateOpen(true)}
                className="inline-flex min-h-11 items-center gap-1.5 rounded-[7px] bg-[#1F4BE0] px-3.5 py-2.5 text-[13px] font-medium text-white hover:bg-[#163AB4]"
              >
                <span className="text-[15px] leading-none">+</span>
                {t("drawings.new")}
              </button>
            ) : null}
          </div>
        </div>

        <div className="mt-3.5 flex items-center gap-5">
          {LIST_TABS.map((tab) => {
            const active = listTab === tab.id;
            const disabled = tab.id !== "all";
            return (
              <button
                key={tab.id}
                type="button"
                disabled={disabled}
                title={
                  disabled ? t("drawings.list_ui.tab_coming_soon") : undefined
                }
                onClick={() => setListTab(tab.id)}
                className={`border-b-2 pb-2.5 text-[13px] ${
                  active
                    ? "border-[#1F4BE0] font-semibold text-[#12161C]"
                    : "border-transparent text-[#6B7482] hover:text-[#12161C]"
                } ${disabled ? "cursor-default opacity-60" : ""}`}
              >
                {t(tab.labelKey)}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex min-h-0 flex-1">
        <aside className="hidden w-[272px] flex-none overflow-y-auto border-r border-[#DCE0E6] bg-[#F7F8FA] px-3.5 py-4 md:block">
          <DrawingStructureTree
            t={t}
            variant="nav"
            projectId={selectedProject.id}
            selectedFloorId={selectedFloorId}
            selectedHouseId={selectedHouseId}
            selectedAndre={effectiveSelectedAndre}
            selectedAll={selectedAll}
            onSelectFloor={handleSelectFloor}
            onSelectHouse={handleSelectHouse}
            onSelectAndre={handleSelectAndre}
            onSelectAll={handleSelectAll}
            totalCount={totalCount}
            unassignedCount={unassignedCount}
            kindsByFloorId={kindsByFloorId}
            showLegend
            manageMode={manageStructure}
          />
        </aside>

        <SheetShell
          open={structureOpen}
          onClose={() => setStructureOpen(false)}
          title={t("drawings.structure.title")}
          heightClass="top-[8%]"
          closeLabel={t("drawings.viewer.mobile.close")}
        >
          <div className="px-3.5 py-3">
            <DrawingStructureTree
              t={t}
              variant="nav"
              projectId={selectedProject.id}
              selectedFloorId={selectedFloorId}
              selectedHouseId={selectedHouseId}
              selectedAndre={effectiveSelectedAndre}
              selectedAll={selectedAll}
              onSelectFloor={(id) => {
                handleSelectFloor(id);
                setStructureOpen(false);
              }}
              onSelectHouse={(id) => {
                handleSelectHouse(id);
                setStructureOpen(false);
              }}
              onSelectAndre={() => {
                handleSelectAndre();
                setStructureOpen(false);
              }}
              onSelectAll={() => {
                handleSelectAll();
                setStructureOpen(false);
              }}
              totalCount={totalCount}
              unassignedCount={unassignedCount}
              kindsByFloorId={kindsByFloorId}
              showLegend
              manageMode={false}
            />
          </div>
        </SheetShell>

        <div className="min-w-0 flex-1 overflow-y-auto px-4 pb-10 pt-4 md:px-6">
          <div className="flex flex-wrap items-center gap-2.5">
            <div className="flex min-h-11 max-w-[420px] flex-1 items-center gap-2 rounded-lg border border-[#D8DDE4] bg-white px-2.5 py-1">
              <MagnifyingGlassIcon className="h-3.5 w-3.5 text-[#9AA1AC]" />
              <input
                type="text"
                placeholder={t("drawings.list_ui.search_placeholder")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1 border-none bg-transparent text-[12.5px] leading-5 outline-none"
              />
            </div>
            <div className="flex max-w-full gap-1 overflow-x-auto rounded-lg border border-[#D8DDE4] bg-white p-0.5">
              <button
                type="button"
                onClick={() => setKindFilter(null)}
                className={`relative flex-shrink-0 rounded-md px-3 py-2 text-[12px] leading-5 md:px-2.5 md:py-0.5 ${
                  !kindFilter
                    ? "bg-[#12161C] text-white"
                    : "text-[#4B5563] hover:text-[#12161C]"
                }`}
              >
                {t("drawings.list_ui.filter_all_types")}
              </button>
              {DRAWING_KINDS.map((kind) => {
                const active = kindFilter === kind;
                return (
                  <button
                    key={kind}
                    type="button"
                    onClick={() => setKindFilter(active ? null : kind)}
                    className={`relative flex-shrink-0 rounded-md px-3 py-2 text-[12px] leading-5 md:px-2.5 md:py-0.5 ${
                      active
                        ? "bg-[#12161C] text-white"
                        : "text-[#4B5563] hover:text-[#12161C]"
                    }`}
                  >
                    {drawingKindLabel(t, kind)}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-3.5 flex items-center gap-2 text-[12px] text-[#6B7482]">
            <span>{scopeLabel}</span>
            <span className="text-[#C2C8D0]">·</span>
            <span>
              {t("drawings.list_ui.result_count", { count: drawings.length })}
            </span>
          </div>

          <div className="mt-3">
            {loading ? (
              <ResourceLoading size="sm" />
            ) : error ? (
              <ResourceError />
            ) : drawings.length === 0 ? (
              <div className="rounded-[10px] border border-dashed border-[#C9D0DA] bg-white px-6 py-11 text-center">
                <div className="text-[15px] font-semibold">
                  {t("drawings.list_ui.empty_title")}
                </div>
                <div className="mt-1.5 text-[13px] text-[#6B7482]">
                  {t("drawings.list_ui.empty_hint")}
                </div>
                {canEditDrawings ? (
                  <button
                    type="button"
                    onClick={() => setCreateOpen(true)}
                    className="mt-4 inline-block rounded-[7px] bg-[#1F4BE0] px-4 py-2.5 text-[13px] font-medium text-white hover:bg-[#163AB4]"
                  >
                    {t("drawings.list_ui.empty_cta")}
                  </button>
                ) : null}
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 xl:grid-cols-3">
                {drawings.map((drawing) => {
                  const { house, floor } = formatDrawingLocation(drawing);
                  const hasFloorId = Boolean(
                    drawing?.drawing_floor?.id || drawing?.drawing_floor_id,
                  );
                  const kind = drawing.drawing_kind || "arkitekt";
                  const style = drawingKindStyle(kind);
                  const location = !hasFloorId
                    ? t("drawings.structure.andre")
                    : [house, floor && `${t("drawings.floor")} ${floor}`]
                        .filter(Boolean)
                        .join(" · ");

                  return (
                    <div
                      key={drawing.id}
                      className="group overflow-hidden rounded-[10px] border border-[#DCE0E6] bg-white shadow-[0_1px_2px_rgba(18,22,28,0.04)] transition hover:border-[#1F4BE0] hover:shadow-[0_6px_20px_rgba(18,22,28,0.09)]"
                    >
                      <Link
                        to={`/dashboard/drawings/${drawing.id}`}
                        className="block"
                      >
                        <div className="relative flex h-[124px] items-center justify-center border-b border-[#E4E7EC] bg-[repeating-linear-gradient(45deg,#F4F6FA_0_8px,#EDF0F6_8px_16px)]">
                          <div className="h-[60%] w-[62%] border-[1.4px] border-[#C3CCDD] bg-[#FBFCFE]" />
                          <span
                            className="absolute left-2.5 top-2.5 rounded px-1.5 py-0.5 text-[10.5px] font-semibold uppercase tracking-[0.04em]"
                            style={{
                              color: style.color,
                              background: "#fff",
                              border: `1px solid ${style.border}`,
                            }}
                          >
                            {drawingKindLabel(t, kind)}
                          </span>
                        </div>
                        <div className="px-3.5 pb-3.5 pt-2.5">
                          <div className="text-[14px] font-semibold tracking-[-0.01em] text-[#12161C]">
                            {drawing.title}
                          </div>
                          <div className="mt-1 text-[12px] text-[#6B7482]">
                            {location}
                          </div>
                          <div className="mt-2.5 text-[11.5px] text-[#8C95A3]">
                            {formatRelativeUpdated(t, drawing.updated_at)}
                          </div>
                        </div>
                      </Link>
                      {canEditDrawings ? (
                        <div className="flex items-center justify-end gap-1 border-t border-[#F1F3F6] px-2 py-1.5 opacity-100 sm:opacity-0 sm:group-hover:opacity-100">
                          <Link
                            to={`/dashboard/drawings/${drawing.id}/edit`}
                            className="grid h-8 w-8 place-items-center rounded-md text-[#6B7482] hover:bg-[#F5F7FA] hover:text-[#12161C]"
                            title={t("common.edit")}
                          >
                            <PencilIcon className="h-4 w-4" />
                          </Link>
                          <button
                            type="button"
                            onClick={() => handleDelete(drawing.id)}
                            className="grid h-8 w-8 place-items-center rounded-md text-[#6B7482] hover:bg-red-50 hover:text-red-600"
                            title={t("common.delete")}
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {canEditDrawings ? (
        <DrawingCreateDrawer
          t={t}
          open={createOpen}
          onClose={() => {
            setCreateOpen(false);
            const next = new URLSearchParams(searchParams);
            next.delete("new");
            setSearchParams(next, { replace: true });
          }}
          project={selectedProject}
          initialFloorId={selectedFloorId}
          initialHouseId={selectedHouseId}
          initialAndre={effectiveSelectedAndre}
          onCreated={async () => {
            await fetchData();
            try {
              const response = await axios({
                method: "get",
                url: "/drawings/",
                params: {
                  project_id: selectedProject.id,
                  per_page: 200,
                  page: 1,
                  fields: "id,drawing_kind,drawing_floor_id,drawing_floor",
                },
              });
              setStructureIndex(response.data?.data || []);
            } catch {
              // best-effort refresh of structure dots/counts
            }
          }}
        />
      ) : null}
    </div>
  );
};
